<template>
  <form novalidate
    @submit.prevent="submit"
    class="event-promotion-form event-promotion-theming-form form-spacing"
  >
    <div
      v-if="id !== null"
      xs6
      class="mb-4"
    >
      <v-select
        v-model="status"
        :items="promotionsStatuses"
        item-title="name"
        item-value="id"
        :label="$t('status')"
        :hide-details="false"
        persistent-hint
        :hint="$t('theming_status_hint')"
        :disabled="!isActivated"
      />
    </div>

    <PromotionEventInformation
      v-if="id !== null"
      :asset="asset"
      :promotion-type="promotionType"
      :core-event="coreEvent"
      :country="country"
    />

    <layout-section-header
      :title="$t('theming_details')"
      :title-right-text="id === null ? $t('step_2_of_2') : ''"
      :has-trigger="false"
    />

    <magic-container class="mb-4">
      <div>
        <div class="d-flex align-center justify-space-between mb-6">
          {{ $t('show_theming_countdown') }}

          <v-switch
            v-model="isCountdown"
            inset
            class="mt-0"
            hide-details
          />
        </div>

        <transition name="slide-down-autocomplete">
          <base-date-picker
            v-if="isCountdown"
            placeholder="Countdown end date"
            :date="countdownEndDateTime"
            :config="dateTimeConfig"
            :error-messages="countdownEndDateTimeErrors"
            :on-change="onChangeCountdownEndDateTime"
            :on-blur="v$.countdownEndDateTime.$touch"
            :hide-details="false"
            :has-clear="false"
            :hint="`(${activePromotion?.country.code} time)`"
            persistent-hint
          />
        </transition>

        <v-text-field
          v-model="title"
          :label="$t('theming_title')"
          :error-messages="titleErrors"
          :hint="$t('event_title_hint')"
          :hide-details="false"
          persistent-hint
          :counter="30"
          :maxlength="30"
        />

        <v-text-field
          v-model="subtitle"
          :label="$t('theming_subtitle')"
          :hint="$t('theming_subtitle_hint')"
          :hide-details="false"
          persistent-hint
          :counter="78"
          :maxlength="78"
        />
      </div>

      <div>
        <!-- by default the color is white -->
        <div :class="`event-promotion-form__preview${activePromotion.asset.color.id === 2 ? '--black' : ''}`">
          <img
            :src="activePromotion.asset.images[0]?.url"
            alt="Event Preview"
            class="event-promotion-form__preview-image"
          >

          <transition name="scale-down">
            <div
              v-if="isCountdown"
              class="event-promotion-form__preview-countdown"
            >
              <v-icon
                size="small"
                :color="activePromotion.asset.color.id === 2 ? 'black' : 'white'"
                class="material-icons-outlined mr-1"
              >
                schedule
              </v-icon>
              Ends {{ getCountdownText() }}
            </div>
          </transition>

          <div class="event-promotion-form__preview-title">
            {{ title?.length > 0 ? title : 'Theming title' }}
          </div>

          <div
            v-if="subtitle?.length > 0"
            class="event-promotion-form__preview-subtitle"
          >
            {{ subtitle }}
          </div>

          <div
            v-if="appliedSponsorships.length > 0"
            class="event-promotion-form__preview-merchants"
          >
            <div class="event-promotion-form__preview-merchants-logos flex-wrap">
              <img
                v-for="(merchant, index) in previewLogos"
                :key="merchant.merchant_id ?? index"
                :src="merchant.sponsorship_logo_url"
                :alt="merchantLabel(merchant)"
                class="event-promotion-form__preview-merchants-logo"
              >
            </div>
            <div class="event-promotion-form__preview-info">
              Sponsored
              <v-icon
                size="small"
                :color="activePromotion.asset.color.id === 2 ? 'black' : 'white'"
                class="material-icons-outlined"
              >
                info
              </v-icon>
            </div>
          </div>

          <div
            v-else
            class="event-promotion-form__preview-merchants__empty"
          >
            No sponsors added
          </div>
        </div>
      </div>
    </magic-container>

    <layout-section-header
      :title="$t('sponsorship').charAt(0).toUpperCase() + $t('sponsorship').slice(1)"
      :has-trigger="false"
      class="mt-8"
    />

    <magic-container v-if="!isActivated">
      <v-autocomplete
        v-model="selectedPlacements"
        :items="promotionsPlacements"
        :item-props="(item) => ({ disabled: checkAllowedPlacements(item) })"
        multiple
        item-title="name"
        item-value="id"
        return-object
        :label="$t('sponsor_placement')"
        :hide-details="false"
        :counter="promotionsPlacements.length"
      />

      <div>
        <v-autocomplete
          v-model="selectedSponsorships"
          multiple
          return-object
          :items="sponsorships"
          :label="$t('sponsorship')"
          type="text"
          item-title="name"
          item-value="id"
          :hide-details="false"
          persistent-hint
          :hint="$t('new_event_sponsorship_hint')"
          @update:model-value="onSelectSponsorship()"
          v-model:search="sponsorshipsSearchInput"
          @update:search="onSponsorshipSearch"
          :counter="5"
        />

        <v-btn
          size="small"
          dense
          variant="outlined"
          class="elevation-0 event-promotion-form__sponsorships-apply-btn"
          color="primary"
          @click="applySponsorship"
        >
          <v-icon size="small">
            add
          </v-icon>
          Apply
        </v-btn>
      </div>
    </magic-container>

    <Removables
      v-if="appliedSponsorships.length > 0"
      :items="appliedSponsorships"
      :on-remove="removeAppliedSponsorship"
      removable-class="event-promotion-form__sponsorships-applied-item"
      :is-disabled="isActivated"
    >
      <template v-slot:item="scope">
        <div>
          <div class="event-promotion-form__sponsorships-applied-item-placements">
            {{ scope.item.placements.map(placement => placement.name).join(', ') }}
          </div>
          <div class="event-promotion-form__sponsorships-applied-item-sponsorships">
            {{ scope.item.merchants.map(merchantLabel).join(', ') }}
          </div>
        </div>
      </template>
    </Removables>

    <layout-section-header
      :title="$t('theming_duration')"
      :has-trigger="false"
      class="mt-8"
    />

    <magic-container>
      <base-date-picker
        placeholder="Theming start date"
        :date="themingStartDate"
        :config="dateConfig"
        :error-messages="themingStartDateErrors"
        :on-change="onChangeThemingStartDate"
        :on-blur="v$.themingStartDate.$touch"
        :hide-details="false"
        persistent-hint
        :hint="$t('when_the_theme_will_go_live')"
        :disabled="id !== null && isActivated"
        :has-clear="false"
      />

      <base-date-picker
        placeholder="Theming end date"
        :date="themingEndDate"
        :config="endDateConfig"
        :error-messages="themingEndDateErrors"
        :on-change="onChangeThemingEndDate"
        :on-blur="v$.themingEndDate.$touch"
        :hide-details="false"
        persistent-hint
        :hint="$t('when_the_theme_will_end')"
        :has-clear="false"
      />
    </magic-container>

    <magic-container>
      <time-select
        :time="themingStartTime"
        :on-change="(v) => themingStartTime = v"
        label="Theming start time"
        :error-messages="themingStartTimeErrors"
        :on-blur="v$.themingStartTime.$touch"
        :attach="false"
        :hide-details="false"
        :disabled="isActivated"
      />

      <time-select
        :time="themingEndTime"
        has-end-of-day
        :on-change="(v) => themingEndTime = v"
        label="Theming end time"
        :error-messages="themingEndTimeErrors"
        :on-blur="v$.themingEndTime.$touch"
        :attach="false"
        :hide-details="false"
        persistent-hint
        hint="(CET time)"
      />
    </magic-container>

    <div class="d-flex justify-space-between mt-4">
      <v-btn
        v-if="id === null"
        variant="outlined"
        @click="back"
        color="primary"
        prepend-icon="chevron_left"
      >
        {{ $t('back') }}
      </v-btn>

      <div class="form-buttons">
        <v-btn
          variant="text"
          @click="cancel"
        >
          {{ $t('cancel') }}
        </v-btn>

        <v-btn
          color="primary"
          class="trigger"
          variant="flat"
          type="submit"
          :loading="isLoading"
        >
          {{ id ? $t('update_event_theming') : $t('create_event_theming') }}
        </v-btn>
      </div>
    </div>
  </form>
</template>

<script>
import Removables from '../../lists/Removables.vue';
import PromotionEventInformation from './PromotionEventInformation.vue';
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { debounce, isObj } from '@/utils';
import { merchantLabel, resolvedMerchants } from '@/utils/sponsorshipMerchants';
import fetchIfNotInStoreMixin from '@/mixins/fetchIfNotInStore';
import { v1 as uuidv1 } from 'uuid';

const sponsorshipsSearchDebouncer = debounce({
  id: uuidv1(),
  timeout: 500
});

export default {
  emits: ['saved'],
  mixins: [fetchIfNotInStoreMixin],
  components: {
    Removables,
    PromotionEventInformation,
  },
  data() {
    return {
      isLoading: false,
      isCountdown: false,
      countdownEndDateTime: '',
      sponsorshipsSearchInput: '',
      title: '',
      subtitle: '',
      selectedPlacements: [],
      selectedSponsorships: [],
      appliedSponsorships: [],
      hasUnresolvedMerchants: false,
      dateTimeConfig: {
        format: 'm/d/Y H:i',
        altFormat: 'm/d/Y H:i',
        time_24hr: true,
        enableTime: true,
        minDate: 'today',
      },
      dateConfig: {
        shorthandCurrentMonth: true,
        minDate: 'today',
      },
      endDateConfig: {
        minDate: this.getMinEndDate(),
      },
      themingStartDate: null,
      themingStartTime: this.getStartTime(),
      themingEndDate: null,
      themingEndTime: '23:59',
      status: null,
      id: null,
      country: null,
      coreEvent: null,
      asset: null,
      promotionType: null,
      isActivated: false,
    };
  },
  setup () {
    return { v$: useVuelidate() }
  },
  validations() {
    return {
      countdownEndDateTime: { required: v => !this.isCountdown || v },
      title: { required },
      themingStartDate: { required },
      themingStartTime: { required },
      themingEndDate: { required },
      themingEndTime: { required },
    };
  },
  computed: {
    ...mapGetters('events', ['promotionsPlacements', 'activePromotion', 'promotionsStatuses']),
    previewLogos() {
      return this.appliedSponsorships
        .flatMap(sponsorship => sponsorship.merchants)
        .filter(merchant => merchant.sponsorship_logo_url);
    },
    sponsorships() {
      const merchants = this.$store.getters['pepper/merchants'];
      const merchantsWithoutSelectedOnes = merchants.filter(merchant => !this.selectedSponsorships.find(sponsorship => sponsorship.id === merchant.id));
      return [...this.selectedSponsorships, ...merchantsWithoutSelectedOnes];
    },
    countdownEndDateTimeErrors() {
      if (!this.v$.countdownEndDateTime.$dirty) return [];
      if (this.$dayjs(this.countdownEndDateTime).isBefore(this.$dayjs())) {
        return [this.$t('date_time_must_be_future')];
      }
      return this.v$.countdownEndDateTime.$errors.map(() => this.$t('required'));
    },
    titleErrors() {
      return this.v$.title.$errors.map(() => this.$t('required'));
    },
    themingStartDateErrors() {
      return this.v$.themingStartDate.$errors.map(() => this.$t('required'));
    },
    themingEndDateErrors() {
      return this.v$.themingEndDate.$errors.map(() => this.$t('required'));
    },
    themingStartTimeErrors() {
      return this.v$.themingStartTime.$errors.map(() => this.$t('required'));
    },
    themingEndTimeErrors() {
      return this.v$.themingEndTime.$errors.map(() => this.$t('required'));
    },
  },
  methods: {
    merchantLabel,
    getMinEndDate() {
      return this.$dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    },
    checkAllowedPlacements(item) {
      if (this.appliedSponsorships.length === 0) return false;
      return -1 !== this.appliedSponsorships.findIndex(sponsorship => -1 !== sponsorship?.placements.findIndex(placement => placement.id === item.id));
    },
    onSponsorshipSearch(search) {
      sponsorshipsSearchDebouncer(() => this.getSponsorships(search));
    },
    onSelectSponsorship() {
      if (this.sponsorshipsSearchInput?.length > 0) {
        this.sponsorshipsSearchInput = ''; // clear search input
      }
      this.getSponsorships();// refresh sponsorships
    },
    getStartTime() {
      return this.$dayjs()
        .add(30 - (this.$dayjs().minute() % 30), 'minutes')
        .format('HH:mm');
    },
    back() {
      // `step`, not `status`: this is the list's query, and its status filter owns that key.
      this.$router.push({ name: 'event-promotion-new', query: { ...this.$route.query, step: 'back' } });
    },
    clear() {
      this.id = null;
      this.isCountdown = false;
      this.countdownEndDateTime = '';
      this.title = '';
      this.subtitle = '';
      this.selectedPlacements = [];
      this.selectedSponsorships = [];
      this.appliedSponsorships = [];
      this.isActivated = false;
      this.themingStartDate = '';
      this.themingStartTime = '';
      this.themingEndDate = '';
      this.themingEndTime = '';

      this.country = null;
      this.coreEvent = null;
      this.asset = null;
      this.promotionType = null;
    },
    applySponsorship() {
      if (
        this.selectedPlacements.length === 0 ||
        this.selectedSponsorships.length === 0 ||
        this.selectedSponsorships.length > 5
      ) {
        return;
      }

      this.appliedSponsorships.push({
        id: this.appliedSponsorships.length + 1,
        placements: this.selectedPlacements,
        merchants: this.selectedSponsorships.map(i => ({ ...i, merchant_id: i.id })),
      });

      // clear the inputs
      this.selectedPlacements = [];
      this.selectedSponsorships = [];
    },
    removeAppliedSponsorship(item) {
      this.appliedSponsorships = this.appliedSponsorships.filter(appliedSponsorship => appliedSponsorship.id !== item.id);
    },
    getSponsorships(search = '') {
      // One character is just the user mid-typing: wait for more instead of alerting.
      if (search?.length === 1) {
        return;
      }

      this.$store.dispatch('pepper/getMerchants', {
        countries: [this.activePromotion.country.code],
        has_logo: true,
        search: search?.length ? search : null,
      });
    },
    getSponsorPlacements() {
      this.$store.dispatch('events/getPromotionsPlacements', {
        country: this.activePromotion.country.code,
      });
    },
    getCountdownText() {
      // fallback if date time does not exist or if it is in the past
      if (!this.countdownEndDateTime || this.$dayjs(this.countdownEndDateTime).isBefore(this.$dayjs())) {
        return ' in ...';
      }

      // check if it's today
      if (this.$dayjs(this.countdownEndDateTime).isSame(this.$dayjs(), 'day')) {
        const endDateTime = this.$dayjs(this.countdownEndDateTime).add(1, 'minute'); // add 1 minute is to round up the time
        // Calculate the duration between the target time and now
        const duration = this.$dayjs.duration(this.$dayjs().diff(endDateTime));
        const totalMinutes = Math.abs(duration.asMinutes());
        const hours = Math.floor(totalMinutes / 60);
        const minutes = Math.floor(totalMinutes % 60);

        return 'in ' + hours + 'h ' + minutes + 'm';
      }

      // if it's not today, let's rely on automatic text
      return this.$dayjs(this.countdownEndDateTime).fromNow();
    },
    cancel() {
      const { step, ...listQuery } = this.$route.query;
      this.$router.push({ name: 'event-promotions', query: listQuery });
    },
    async submit() {
      if (this.isLoading) return;

      if (this.hasUnresolvedMerchants) {
        this.$store.dispatch('alerts/set', {
          data: {
            message: 'Some sponsor merchants could not be loaded. Reload the page before saving, or they would be removed.',
          },
          alertType: 'error',
          report: false,
        });
        return;
      }

      this.isLoading = true;
      const isValid = await this.v$.$validate();
      if (!isValid) {
        this.isLoading = false;
        return;
      }

      const action = this.id !== null ? 'updatePromotion' : 'createPromotion';

      const payload = {
        // fields from first step
        country: this.activePromotion.country.code,
        core_event_id: this.activePromotion.event.id,
        event_asset_id: this.activePromotion.asset.id,
        type: 1, // 1 = theming, 2 = banner

        // fields from second step
        show_carousel: 0, // needed for banner only
        title: this.title,
        subtitle: this.subtitle,
        start_date: this.themingStartDate + ' ' + this.themingStartTime,
        end_date: this.themingEndDate + ' ' + this.themingEndTime,
      };

      if (this.appliedSponsorships.length !== 0) {
        payload.sponsorships = this.appliedSponsorships.map(sponsorship => {
          return {
            placements: sponsorship.placements.map(placement => placement.id),
            merchants: sponsorship.merchants.map(merchant => merchant.merchant_id),
          };
        });
      } else {
        payload.sponsorships = null;
      }

      if (this.id !== null) {
        payload.id = this.id;
        payload.status = this.status;
      }

      if (this.isCountdown) {
        payload.countdown_date = this.countdownEndDateTime;
      } else {
        payload.countdown_date = null;
      }

      this.$store.dispatch(`events/${action}`, payload).then(() => {
        this.$emit('saved');
        const { step, ...listQuery } = this.$route.query;
        this.$router.push({ name: 'event-promotions', query: listQuery });
      })
        .catch((err) => err)
        .finally(() => {
          this.isLoading = false;
        });
    },
    onChangeCountdownEndDateTime(date) {
      this.countdownEndDateTime = date;
    },
    onChangeThemingStartDate(date) {
      this.themingStartDate = date;
    },
    onChangeThemingEndDate(date) {
      this.themingEndDate = date;
    },
    async onReceiveItem(data) {
      this.id = data.id;
      this.isActivated = data.activated?.id === 1;
      this.title = data.title;
      this.subtitle = data.subtitle;
      this.country = data.country;
      this.hasUnresolvedMerchants = false;
      this.appliedSponsorships = data.sponsorships.map((sponsorship, index) => {
        const { merchants, hasUnresolved } = resolvedMerchants(sponsorship.merchants);
        this.hasUnresolvedMerchants = this.hasUnresolvedMerchants || hasUnresolved;

        return {
          id: index,
          placements: sponsorship.placements,
          merchants,
        };
      });
      this.status = data.status.id;
      this.coreEvent = data.event;
      this.asset = data.asset;
      this.promotionType = data.type;
      this.countdownEndDateTime = data.countdown_date === null ? '' : data.countdown_date;
      this.isCountdown = data.countdown_date !== null;
      this.themingStartDate = data.start_date.split(' ')[0];
      this.themingStartTime = data.start_date.split(' ')[1].trim();
      this.themingEndDate = data.end_date.split(' ')[0];
      this.themingEndTime = data.end_date.split(' ')[1].trim();

      await this.$store.dispatch('events/setActivePromotion', data);

      this.getSponsorships();
      this.getSponsorPlacements();
      this.getPromotionsStatuses();
    },
    getPromotionsStatuses() {
      this.$store.dispatch('events/getPromotionsStatuses', {
        country: this.country.code,
      });
    },
  },
  watch: {
    '$route.params.id': {
      handler(id) {
        if (id) {
          this.fetchIfNotInStore(id,'events', 'promotions', 'getPromotion')
            .then((data) => {
              this.onReceiveItem(data);
            }, () => this.leaveMissingItem(id));
        }
      },
      deep: true,
      immediate: true,
    },
  },
  created() {
    if (isObj(this.activePromotion) && !this.$route.params.id) {
      this.getSponsorships();
      this.getSponsorPlacements();
    }
  },
};
</script>

<style lang="scss">
.event-promotion-form__sponsorships-apply-btn {
  float: right !important;
  max-height: 24px !important;
  min-height: 24px !important;
}

.event-promotion-form__sponsorships-applied-item {
  border: 1px solid #E0E0E0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0 !important;
  padding-bottom: 0 !important;

  &.v-list-item {
    min-height: 54px;
  }

  .v-list-item__content {
    padding: 8px 0;
    width: 100%;
  }
}

.event-promotion-form__sponsorships-applied-item-placements {
  color: #616161;
  font-size: 12px;
  font-weight: 400;
  margin-bottom: 4px;
  line-height: 15px;

  .v-theme--dark & {
    color: rgba(var(--v-theme-on-surface), 0.6);
  }
}

.event-promotion-form__sponsorships-applied-item-sponsorships {
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
}
</style>
